import { get } from 'lodash';
import { apiClient, handleError } from '../api/ApiClient';
import {
  FETCH_AGGREGATED_STATISTICS_SUCCESS,
  FETCH_CUSTOMER_STATISTICS_SUCCESS,
  FETCH_FEES_STATISTICS_SUCCESS,
  FETCH_PURCHASE_STATISTICS_SUCCESS,
  FETCH_SALE_STATISTICS_SUCCESS,
  FETCH_SNAPSHOT_STATISTICS_SUCCESS,
} from './DashboardReducer';
import { getDateDaysAgo, getTodayDate } from '../common/utils/date/Date.utils';

const STATISTICS = '/statistics';
const SNAPSHOT_STATISTICS = `${STATISTICS}/snapshot`;
const CUSTOMER_STATISTICS = `${STATISTICS}/customer`;
const SALE_STATISTICS = `${STATISTICS}/sale`;
const FEES_STATISTICS = `${STATISTICS}/fees`;
const PURCHASE_STATISTICS = `${STATISTICS}/purchase`;

export const fetchAggregatedStatistics = (callback) => async (dispatch) => {
  const { snapshotStatPayload, customerStatPayload, saleStatPayload, feesStatPayload } =
    prepareAggregatedStatisticsPayload();

  const snapshotStatResponse = await apiClient.post(SNAPSHOT_STATISTICS, snapshotStatPayload);
  const customerStatResponse = await apiClient.post(CUSTOMER_STATISTICS, customerStatPayload);
  const saleStatResponse = await apiClient.post(SALE_STATISTICS, saleStatPayload);
  const feesStatResponse = await apiClient.post(FEES_STATISTICS, feesStatPayload);
  const purchaseStatResponse = await apiClient.post(PURCHASE_STATISTICS);

  const success = [
    snapshotStatResponse.status,
    customerStatResponse.status,
    saleStatResponse.status,
    feesStatResponse.status,
    purchaseStatResponse.status,
  ].every((status) => status === 200);

  if (success) {
    callback();
    dispatch({
      type: FETCH_AGGREGATED_STATISTICS_SUCCESS,
      payload: {
        snapshotStatistics: get(snapshotStatResponse, 'data.data', null),
        customerStatistics: get(customerStatResponse, 'data.data', null),
        saleStatistics: get(saleStatResponse, 'data.data', null),
        feesStatistics: get(feesStatResponse, 'data.data', null),
        purchaseStatistics: get(purchaseStatResponse, 'data.data', null),
      },
    });
  }
};

export const fetchSnapshotStatistics = (values) => (dispatch) =>
  apiClient
    .post(SNAPSHOT_STATISTICS, values)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_SNAPSHOT_STATISTICS_SUCCESS,
          payload: {
            snapshotStatistics: get(res, 'data.data', null),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchCustomerStatistics = (values) => (dispatch) =>
  apiClient
    .post(CUSTOMER_STATISTICS, values)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_CUSTOMER_STATISTICS_SUCCESS,
          payload: {
            customerStatistics: get(res, 'data.data', null),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchSaleStatistics = (values) => (dispatch) =>
  apiClient
    .post(SALE_STATISTICS, values)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_SALE_STATISTICS_SUCCESS,
          payload: {
            saleStatistics: get(res, 'data.data', null),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchFeesStatistics = (values) => (dispatch) =>
  apiClient
    .post(FEES_STATISTICS, values)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_FEES_STATISTICS_SUCCESS,
          payload: {
            feesStatistics: get(res, 'data.data', null),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchPurchaseStatistics = (values) => (dispatch) =>
  apiClient
    .post(PURCHASE_STATISTICS, values)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_PURCHASE_STATISTICS_SUCCESS,
          payload: {
            purchaseStatistics: get(res, 'data.data', null),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

const prepareAggregatedStatisticsPayload = () => {
  const today = getTodayDate();
  const yesterday = getDateDaysAgo(1);
  const sevenDaysAgo = getDateDaysAgo(7);
  const thirtyDaysAgo = getDateDaysAgo(30);

  const snapshotStatPayload = {
    newBuyersRangeStart: today,
    newBuyersRangeEnd: today,
    newBuyersCompareStart: yesterday,
    newBuyersCompareEnd: yesterday,
    newSellersRangeStart: today,
    newSellersRangeEnd: today,
    newSellersCompareStart: yesterday,
    newSellersCompareEnd: yesterday,
    boughtVehiclesRangeStart: sevenDaysAgo,
    boughtVehiclesRangeEnd: today,
  };

  const customerStatPayload = {
    buyersWithMaxOneBoughtVehicleRangeStart: thirtyDaysAgo,
    buyersWithMaxOneBoughtVehicleRangeEnd: today,
    sellersWithMaxOneSoldVehicleRangeStart: thirtyDaysAgo,
    sellersWithMaxOneSoldVehicleRangeEnd: today,
  };

  const saleStatPayload = {
    soldVehiclesRangeStart: today,
    soldVehiclesRangeEnd: today,
    totalSalesRangeStart: today,
    totalSalesRangeEnd: today,
    auctionsWithNoSaleRangeStart: today,
    auctionsWithNoSaleRangeEnd: today,
    salesVersusCapRangeStart: today,
    salesVersusCapRangeEnd: today,
  };

  const feesStatPayload = {
    sellersFeesRangeStart: today,
    sellersFeesRangeEnd: today,
    buyersFeesRangeStart: today,
    buyersFeesRangeEnd: today,
    buyersSubscriptionFeesRangeStart: today,
    buyersSubscriptionFeesRangeEnd: today,
    sellersSubscriptionFeesRangeStart: today,
    sellersSubscriptionFeesRangeEnd: today,
    totalBidsPlacedRangeStart: today,
    totalBidsPlacedRangeEnd: today,
    averageBidsNumberRangeStart: today,
    averageBidsNumberRangeEnd: today,
    averageValueOfSoldVehiclesRangeStart: today,
    averageValueOfSoldVehiclesRangeEnd: today,
  };

  return {
    snapshotStatPayload,
    customerStatPayload,
    saleStatPayload,
    feesStatPayload,
  };
};
